/**
 * Single "Report" button anchored at the bottom of a commentary response.
 * Opens the report dialog populated with the SKJV It Is Written markdown.
 */

import { useMemo, useState } from 'react';
import { WarningIcon, CheckIcon } from '@components/icons';
import { submitCommentaryFeedback } from '@/api';
import { buildReportMarkdown, type ReportInput } from './build-report-markdown';
import { CommentaryReportDialog } from './commentary-report-dialog';

interface ReportButtonProps {
  input: ReportInput;
  originalQuery?: string;
}

export function ReportButton({ input, originalQuery }: ReportButtonProps) {
  const [open, setOpen] = useState(false);
  const [reported, setReported] = useState(false);

  const initialMarkdown = useMemo(() => buildReportMarkdown(input), [input]);

  const handleSubmit = async (markdown: string) => {
    await submitCommentaryFeedback({
      verseRef: input.reference,
      originalQuery,
      reportMarkdown: markdown,
      commentaryText: input.aiResponse,
    });
    setReported(true);
    setTimeout(() => setReported(false), 2500);
  };

  return (
    <>
      <div className="report-button-row">
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="report-button"
          title="Report this response"
        >
          {reported ? (
            <CheckIcon className="h-3.5 w-3.5 text-emerald-500" />
          ) : (
            <WarningIcon className="h-3.5 w-3.5" />
          )}
          {reported ? 'Reported' : 'Report'}
        </button>
      </div>

      {open && (
        <CommentaryReportDialog
          verseRef={input.reference}
          initialMarkdown={initialMarkdown}
          onSubmit={handleSubmit}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}
